import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * SectionContextBar — Fascia contestuale evoluta di sezione.
 *
 * v1.11.0 · UX Evolution v3 · Fase A (26/07/2026)
 * =================================================
 * Evoluzione della vecchia `kpiStrip` di <ModulePageLayout />.
 * Sostituisce le pillole staccate con UNA SUPERFICIE UNICA compatta
 * che integra: icona modulo + titolo + descrizione breve + KPI
 * inline + eventuale stato critico + una singola azione primaria.
 *
 * ── Filosofia (richiesta utente 26/07/2026)
 *   "Non deve diventare una fila di card. Deve essere un'unica
 *    superficie compatta, con gerarchia chiara e KPI integrati
 *    lateralmente."
 *
 * ── Anatomia orizzontale (desktop, ≥ 1024px)
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [icon] TITOLO           23 KPI-1  ·  4 KPI-2  ·  10 KPI-3   │
 *   │        descrizione                              [⚠ avviso]   │
 *   └──────────────────────────────────────────────────────────────┘
 *   Altezza: 64px (72px se `criticalNotice`).
 *
 * ── Degradazione mobile (< 768px)
 *   - Icona rimane a sinistra
 *   - Titolo + descrizione impilate
 *   - KPI scorrono in orizzontale con overflow-x
 *   - Azione primaria si compatta a solo-icona
 *
 * Props:
 *   - icon:            componente lucide-react (h-5 w-5)
 *   - title:           titolo sezione (obbligatorio)
 *   - description:     breve descrizione (una riga)
 *   - kpis:            array di { value, label, tone?, testid? }
 *                        tone: 'default' | 'success' | 'warning' | 'danger' | 'info'
 *   - criticalNotice:  { message, onClick? } - se presente altezza aumenta
 *   - primaryAction:   { label, onClick, icon?, testid? } - una sola
 *   - testid:          data-testid del root
 */
export function SectionContextBar({
  icon: Icon,
  title,
  description,
  kpis = [],
  criticalNotice,
  primaryAction,
  className,
  testid,
}) {
  const hasCritical = !!criticalNotice;
  const heightVar = hasCritical
    ? "var(--suite-context-h-lg)"
    : "var(--suite-context-h)";

  return (
    <section
      className={cn(
        "suite-context-bar w-full flex items-center gap-4",
        className,
      )}
      style={{
        minHeight: heightVar,
        maxHeight: heightVar,
        backgroundColor: "var(--suite-surface)",
        borderBottom: "1px solid var(--suite-border)",
        paddingLeft: "var(--suite-sp-6)",
        paddingRight: "var(--suite-sp-6)",
        paddingTop: "var(--suite-sp-2)",
        paddingBottom: "var(--suite-sp-2)",
      }}
      data-testid={testid || "section-context-bar"}
      aria-label={title}
    >
      {/* ── ICONA MODULO ────────────────────────────────── */}
      {Icon && (
        <span
          className="shrink-0 flex items-center justify-center rounded-md"
          style={{
            width: 34,
            height: 34,
            backgroundColor: "var(--gis-green-50)",
            color: "var(--gis-green)",
          }}
          aria-hidden="true"
        >
          <Icon className="h-[18px] w-[18px]" />
        </span>
      )}

      {/* ── TITOLO + DESCRIZIONE ────────────────────────── */}
      <div className="min-w-0 flex flex-col justify-center leading-tight shrink-0">
        <h2
          className="gis-heading font-bold truncate"
          style={{
            fontSize: 17,
            color: "var(--suite-ink-900)",
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}
          data-testid={testid ? `${testid}-title` : "section-context-title"}
        >
          {title}
        </h2>
        {description && (
          <p
            className="truncate"
            style={{
              fontSize: 12,
              color: "var(--suite-ink-500)",
              marginTop: 2,
              maxWidth: 340,
            }}
          >
            {description}
          </p>
        )}
      </div>

      {/* Separatore verticale (solo desktop) */}
      {kpis.length > 0 && (
        <span
          className="hidden md:block shrink-0"
          style={{
            width: 1,
            height: 32,
            backgroundColor: "var(--suite-divider)",
          }}
          aria-hidden="true"
        />
      )}

      {/* ── KPI INTEGRATI (row inline, nessuna card) ────── */}
      {kpis.length > 0 && (
        <div
          className="flex items-center gap-5 min-w-0 flex-1 overflow-x-auto"
          data-testid={testid ? `${testid}-kpis` : "section-context-kpis"}
        >
          {kpis.map((kpi, idx) => (
            <SectionKpi key={idx} {...kpi} />
          ))}
        </div>
      )}

      {/* ── STATO CRITICO / AVVISO ──────────────────────── */}
      {hasCritical && (
        <button
          type="button"
          onClick={criticalNotice.onClick}
          className={cn(
            "shrink-0 flex items-center gap-1.5 rounded-md transition-colors",
            criticalNotice.onClick && "cursor-pointer",
          )}
          disabled={!criticalNotice.onClick}
          style={{
            padding: "4px 10px",
            height: 30,
            backgroundColor: "rgba(177, 42, 42, 0.08)",
            color: "var(--suite-danger)",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "var(--gis-font-heading)",
          }}
          data-testid={testid ? `${testid}-critical` : "section-context-critical"}
          title={criticalNotice.message}
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate max-w-[200px]">{criticalNotice.message}</span>
        </button>
      )}

      {/* ── AZIONE PRIMARIA (opzionale, UNA sola) ───────── */}
      {primaryAction && (
        <Button
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          className="gis-btn-primary shrink-0 h-9"
          data-testid={primaryAction.testid || "section-context-primary-action"}
        >
          {primaryAction.icon && (
            <primaryAction.icon className="h-4 w-4 mr-1.5" />
          )}
          {primaryAction.label}
        </Button>
      )}
    </section>
  );
}

/**
 * SectionKpi — singolo KPI inline (NON è una card).
 * Layout: numero grande a sinistra + label discreta a destra, nessun bordo.
 */
function SectionKpi({
  value,
  label,
  tone = "default",
  onClick,
  testid,
}) {
  const toneColor =
    {
      default: "var(--suite-ink-900)",
      success: "var(--suite-success)",
      warning: "var(--suite-warning)",
      danger: "var(--suite-danger)",
      info: "var(--suite-info)",
      brand: "var(--gis-green)",
    }[tone] || "var(--suite-ink-900)";

  const isButton = typeof onClick === "function";
  const Tag = isButton ? "button" : "div";

  return (
    <Tag
      type={isButton ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "shrink-0 flex items-baseline gap-1.5 whitespace-nowrap",
        isButton && "cursor-pointer transition-opacity hover:opacity-70",
      )}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
      }}
      data-testid={testid}
    >
      <span
        className="gis-heading font-bold leading-none"
        style={{
          fontSize: 20,
          color: toneColor,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
      <span
        className="leading-none"
        style={{
          fontSize: 11,
          color: "var(--suite-ink-500)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
    </Tag>
  );
}

export { SectionKpi };
export default SectionContextBar;

